/* -----------------------------------------------------------------------------------------------------
  - Created: [13-02-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains equipment form
  -------------------------------------------------------------------------------------------------------
*/
import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/joy';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CustomTextFieldWithIcon from '../../../../Components/custom_components/custom_text_field/CustomTextFieldWithIcon';
import ActionButton from '../../../../Components/custom_components/CustomButtons/ActionButton/ActionButton';
import './EquipmentFormStyle.css';
import { FaHashtag } from "react-icons/fa";
import { BsFillBuildingsFill } from "react-icons/bs";
import { TbFileDescription } from 'react-icons/tb';
import CustomTextArea from '../../../../Components/custom_components/CustomTextArea/CustomTextArea';
import { CiBarcode } from "react-icons/ci";
import { BiPurchaseTag } from "react-icons/bi";
import { get_technology_with_out_pagination, get_technology_with_pagination } from '../../../../config/Resource/technologyApis';
import { TfiShortcode } from 'react-icons/tfi';
import CustomSelectDropdown from '../../../../Components/custom_components/CustomSelectDropdown/CustomSelectDropdown';
import dayjs from "dayjs";
import CustomDatePicker from '../../../../Components/custom_components/CustomDatePicker/CustomDatePicker';
import { CgCalendarDates } from 'react-icons/cg';
import CustomTooltip from '../../../../Components/custom_components/CustomTooltip/CustomTooltip';
import { statusOptions } from '../../../../utils/constants';


function EquipmentForm({ handleSave, isButtonDisabled, id, formValues, isDisabled }) {
    const [technologyListing, setTechnologyListing] = useState([]);

    // FOR GET TECHNOLOGIES FROM LIST
    useEffect(() => {
        const fetchTechnologies = async () => {
            try {
                const params = {
                    status: 1 //1MEANS ACTIVE 
                }

                const response = await get_technology_with_pagination(params);
                if (response?.status === "success") {
                    // Transform data into the desired format with label and value
                    const transformedData = response.data.data?.map((item) => ({
                        label: item?.TechnologiesName,
                        value: item?.TechnologiesTypeId
                    }));

                    // Set the transformed data to the state
                    setTechnologyListing(transformedData || []);
                } else {
                    setTechnologyListing([]);
                }
            } catch (error) {
                console.error('Error fetching technologies:', error);
                setTechnologyListing([]);
            }
        };

        fetchTechnologies();
    }, []);


    const formik = useFormik({
        initialValues: formValues,

        validationSchema: Yup.object({
            EquipmentName: Yup.string().required(' Name is required'),
            EquipmentserialNo: Yup.string().required('Serial No is required')
                .max(10, 'Serial No cannot exceed 10 characters'),

            // EquipmentCalibrationDate: Yup.string()
            //     .required('Calibration Date is required'),

            // EquipmentCalibrationDate: Yup.date().required('Calibration Date is required'),
            Name_of_Manufacturer: Yup.string().required('Manufacturer is required'),
            technology_ids: Yup.array().min(1, 'At least one Technology must be selected'),


            EquipmentPurchaseYear: Yup.number().required('Purchase Year is required')
                .min(1900, 'Invalid Year')
                .max(9999, 'Invalid Year'),
            EquipmentShortcode: Yup.string().required('Code is required')
                .min(4, 'Code Code must be at least 4 characters')
                .max(10, 'Code must not exceed 10 characters.'),
            EquipmentStatus: Yup.string().required('Status is required'),
            EquipmentExpiry: Yup.string()
                .required('Expiry Date is required')
        }),
        onSubmit: (values) => {
            handleSave(values, id)
        },
    });

    // Reset handler
    const handleReset = () => {
        formik.setFieldValue('EquipmentName', '');
        formik.setFieldValue('EquipmentShortcode', '');
        formik.setFieldValue('EquipmentserialNo', '');
        formik.setFieldValue('EquipmentCalibrationDate', null);
        formik.setFieldValue('technology_ids', []); // Reset to an empty array
        formik.setFieldValue('EquipmentPurchaseYear', '');
        formik.setFieldValue('EquipmentDescription', '');
        formik.setFieldValue('EquipmentStatus', 1); // Default value for STATUS
        formik.setFieldValue('EquipmentExpiry', null);
        formik.setFieldValue('Name_of_Manufacturer', '');


        // Reset warning messages (touched fields)
        formik.setTouched({
            EquipmentName: false,
            EquipmentShortcode: false,
            EquipmentserialNo: false,
            EquipmentCalibrationDate: false,
            technology_ids: false,
            EquipmentPurchaseYear: false,
            EquipmentDescription: false,
            EquipmentStatus: false,
            EquipmentExpiry: false,
            Name_of_Manufacturer: false,
        });
    };

    return (
        <div className="equipmentform-form-container">
            <Grid container spacing={1}>
                {/* Equipment Name */}
                <Grid item xs={12} md={6}>
                    <div className="equipmentform-mid-form-container">
                        <div className="label-container">
                            <span className="label"> Name<span className="label_required_remark">*</span></span>
                        </div>
                        <CustomTooltip title={isDisabled ? formik.values.EquipmentName : ''}>
                            <div className="custom-textfield-container">
                                <CustomTextFieldWithIcon
                                    id="EquipmentName"
                                    name="EquipmentName"
                                    placeholder="Enter Name"
                                    value={formik.values.EquipmentName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.EquipmentName && Boolean(formik.errors.EquipmentName)}
                                    helperText={formik.touched.EquipmentName && formik.errors.EquipmentName}
                                    StartIcon={BsFillBuildingsFill}
                                    isDisabled={isDisabled}
                                />
                            </div>
                        </CustomTooltip>

                    </div>
                </Grid>

                <Grid item xs={12} md={6}>
                    <div className="equipmentform-mid-form-container">
                        <div className="label-container">
                            <span className="label"> Code<span className="label_required_remark">*</span></span>
                        </div>
                        <div className="custom-textfield-container">
                            <CustomTextFieldWithIcon
                                id='EquipmentShortcode'
                                name='EquipmentShortcode'
                                placeholder='Enter Code'
                                value={formik?.values?.EquipmentShortcode}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.EquipmentShortcode && Boolean(formik.errors.EquipmentShortcode)}
                                helperText={formik.touched.EquipmentShortcode && formik.errors.EquipmentShortcode}
                                isDisabled={isDisabled}
                                maxLength={10}
                            />
                        </div>
                    </div>
                </Grid>

                {/* Serial No */}
                <Grid item xs={12} md={6}>
                    <div className="equipmentform-mid-form-container">
                        <div className="label-container">
                            <span className="label">Serial No<span className="label_required_remark">*</span></span>
                        </div>
                        <div className="custom-textfield-container">
                            <CustomTextFieldWithIcon
                                id="EquipmentserialNo"
                                name="EquipmentserialNo"
                                placeholder="Enter Serial No"
                                value={formik.values.EquipmentserialNo}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.EquipmentserialNo && Boolean(formik.errors.EquipmentserialNo)}
                                helperText={formik.touched.EquipmentserialNo && formik.errors.EquipmentserialNo}
                                StartIcon={CiBarcode}
                                isDisabled={isDisabled}
                            />
                        </div>
                    </div>
                </Grid>

                {/* Name of Manufacturer */}
                <Grid item xs={12} md={6}>
                    <div className="equipmentform-mid-form-container">
                        <div className="label-container">
                            <span className="label"> Name of Manufacturer <span className="label_required_remark">*</span></span>
                        </div>
                        <CustomTooltip title={isDisabled ? formik.values.Name_of_Manufacturer : ''}>
                            <div className="custom-textfield-container">
                                <CustomTextFieldWithIcon
                                    id="Name_of_Manufacturer"
                                    name="Name_of_Manufacturer"
                                    placeholder="Enter Manufacturer Name"
                                    value={formik.values.Name_of_Manufacturer}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.Name_of_Manufacturer && Boolean(formik.errors.Name_of_Manufacturer)}
                                    helperText={formik.touched.Name_of_Manufacturer && formik.errors.Name_of_Manufacturer}
                                    StartIcon={BsFillBuildingsFill}
                                    isDisabled={isDisabled}
                                />
                            </div>
                        </CustomTooltip>

                    </div>
                </Grid>

                {/* Calibration Date */}
                <Grid item xs={12} md={6}>
                    <div className="equipmentform-mid-form-container">
                        <div className="label-container">
                            <span className="label">Calibration Date
                                {/* <span className="label_required_remark">*</span> */}
                            </span>
                        </div>
                        <div className="custom-textfield-container">
                            <CustomDatePicker
                                name="EquipmentCalibrationDate"
                                onChange={(date) => formik.setFieldValue("EquipmentCalibrationDate", date)}
                                onBlur={formik.handleBlur}
                                value={dayjs(formik.values.EquipmentCalibrationDate, "DD-MM-YYYY")} //DD-MM-YYYY
                                error={formik.touched.EquipmentCalibrationDate && Boolean(formik.errors.EquipmentCalibrationDate)}
                                helperText={formik.touched.EquipmentCalibrationDate && formik.errors.EquipmentCalibrationDate}
                                StartIcon={CgCalendarDates}
                                isDisabled={isDisabled}
                            />
                            {/* <CustomTextFieldWithIcon
                                id="EquipmentCalibrationDate"
                                name="EquipmentCalibrationDate"
                                type="date"
                                placeholder="Select Calibration Date"
                                value={formik.values.EquipmentCalibrationDate}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.EquipmentCalibrationDate && Boolean(formik.errors.EquipmentCalibrationDate)}
                                helperText={formik.touched.EquipmentCalibrationDate && formik.errors.EquipmentCalibrationDate}
                                StartIcon={FaRegCalendar}
                                isDisabled={isDisabled}
                            /> */}
                        </div>
                    </div>
                </Grid>

                {/* Technology ID */}
                <Grid item xs={12} md={6}>
                    <div className="equipmentform-mid-form-container">
                        <div className="label-container">
                            <span className="label">Technology <span className="label_required_remark">*</span></span>
                        </div>
                        <div className="custom-textfield-container">
                            <CustomSelectDropdown
                                name="technology_ids"
                                placeholder="Select Technology"
                                options={technologyListing}
                                value={technologyListing?.filter(option => formik.values.technology_ids?.includes(option.value))}
                                onChange={(selectedOptions) => {
                                    // Map selected options to extract only the `value` (ID) field
                                    const selectedValues = selectedOptions.map(option => option.value);
                                    // Set the field value with the array of selected IDs
                                    formik.setFieldValue("technology_ids", selectedValues);
                                }}
                                onBlur={formik.handleBlur}
                                error={formik.touched.technology_ids && Boolean(formik.errors.technology_ids)}
                                helperText={formik.touched.technology_ids && formik.errors.technology_ids}
                                isDisabled={isDisabled}
                                isMulti={true} // Set to true for multi-select
                                width="100%"
                                StartIcon={<FaHashtag />}
                            />
                        </div>
                    </div>
                </Grid>

                {/* Manufacturer */}
                {/* <Grid item xs={12} md={6}>
                    <div className="equipmentform-mid-form-container">
                        <div className="label-container">
                            <span className="label">Manufacturer<span className="label_required_remark">*</span></span>
                        </div>
                        <div className="custom-textfield-container">
                            <CustomAutoCompleteDropdown
                                name="manufacturer"
                                placeholder="Select Manufacturer"
                                options={manufacturers}
                                value={manufacturers.find(
                                    (option) => option.value === formik.values.manufacturer
                                ) || null}
                                onChange={(selectedOption) => {
                                    formik.setFieldValue("manufacturer", selectedOption ? selectedOption.value : "");
                                }}
                                onBlur={formik.handleBlur}
                                width="100%"
                                error={formik.touched.manufacturer && Boolean(formik.errors.manufacturer)}
                                helperText={formik.touched.manufacturer && formik.errors.manufacturer}
                                StartIcon={<MdPrecisionManufacturing />}
                            />
                        </div>
                    </div>
                </Grid> */}

                {/* Equipment Purchase Year */}
                <Grid item xs={12} md={6}>
                    <div className="equipmentform-mid-form-container">
                        <div className="label-container">
                            <span className="label"> Purchase Year<span className="label_required_remark">*</span></span>
                        </div>
                        <div className="custom-textfield-container">
                            <CustomTextFieldWithIcon
                                id="EquipmentPurchaseYear"
                                name="EquipmentPurchaseYear"
                                type="number"
                                placeholder="Enter Purchase Year"
                                value={formik.values.EquipmentPurchaseYear}
                                onChange={(e) => {
                                    const { value } = e.target;
                                    if (Number(value) === 0) {
                                        formik.setFieldValue('EquipmentPurchaseYear', '');
                                    } else {
                                        formik.handleChange(e);
                                    }
                                }}
                                onBlur={formik.handleBlur}
                                error={formik.touched.EquipmentPurchaseYear && Boolean(formik.errors.EquipmentPurchaseYear)}
                                helperText={formik.touched.EquipmentPurchaseYear && formik.errors.EquipmentPurchaseYear}
                                isDisabled={isDisabled}
                            />
                        </div>
                    </div>
                </Grid>

                {/* Equipment Expiry */}
                <Grid item xs={12} md={6}>
                    <div className="equipmentform-mid-form-container">
                        <div className="label-container">
                            <span className="label">Expiry Date
                                <span className="label_required_remark">*</span>
                            </span>
                        </div>
                        <div className="custom-textfield-container">

                            <CustomDatePicker
                                name="EquipmentExpiry"
                                onChange={(date) => formik.setFieldValue("EquipmentExpiry", date)}
                                onBlur={formik.handleBlur}
                                value={dayjs(formik.values.EquipmentExpiry, "DD-MM-YYYY")}
                                error={formik.touched.EquipmentExpiry && Boolean(formik.errors.EquipmentExpiry)}
                                helperText={formik.touched.EquipmentExpiry && formik.errors.EquipmentExpiry}
                                isDisabled={isDisabled}
                            />
                        </div>
                    </div>
                </Grid>

                {id && <Grid item xs={12} md={6}>
                    <div className="equipmentform-mid-form-container">
                        <div className="label-container">
                            <span className="label">Status<span className="label_required_remark">*</span></span>
                        </div>
                        <div className="custom-textfield-container">
                            <CustomSelectDropdown
                                name="EquipmentStatus"
                                placeholder="Select Status"
                                options={statusOptions}
                                value={statusOptions?.find(option => option?.value === formik?.values?.EquipmentStatus) || ""}
                                onChange={(selectedOption) => {
                                    formik.setFieldValue("EquipmentStatus", selectedOption ? selectedOption.value : "");
                                }}
                                onBlur={formik.handleBlur}
                                error={formik.touched.EquipmentStatus && Boolean(formik.errors.EquipmentStatus)}
                                helperText={formik.touched.EquipmentStatus && formik.errors.EquipmentStatus}
                                isDisabled={isDisabled}
                                isMulti={false} // Set to false for single-select
                                width="100%"
                                isClearable={false}
                            />
                        </div>
                    </div>
                </Grid>}

                <Grid item xs={12} md={12}>
                    <div className="label-container">
                        <span className="label">Description </span>
                    </div>
                    <div className="custom-textfield-container">
                        <CustomTextArea
                            name="EquipmentDescription"
                            placeholder="Enter your text here"
                            value={formik.values.EquipmentDescription}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            rows={5}
                            error={formik.touched.EquipmentDescription && Boolean(formik.errors.EquipmentDescription)}
                            helperText={formik.touched.EquipmentDescription && formik.errors.EquipmentDescription}
                            isDisabled={isDisabled}
                        />
                    </div>
                </Grid>
            </Grid>

            {/* Action Buttons */}
            {!isDisabled && <div className="form-bottom-action-buttons-container">
                <ActionButton onClick={handleReset} label="Reset" btnType="outlined" />
                <ActionButton btnDisable={isButtonDisabled} onClick={formik.handleSubmit} label="Save" btnType="primary" />
            </div>}
        </div>
    );
}

export default EquipmentForm;
